import {
  Business as BusinessIcon,
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Language as LanguageIcon,
  MoreVert as MoreVertIcon,
  PeopleOutline as PeopleIcon,
  PersonOutline as PersonIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import LoadingButton from "../../components/LoadingButton";
import { hostname, parseError } from "../../utils";

interface Whitelabel {
  id: string;
  name: string;
  domain: string | null;
  uid: string;
  ownerEmail: string | null;
  ownerName: string | null;
  active: boolean;
  userCount: number;
  companyCount: number;
  domainStatus: string | null;
  domainProvisioned: boolean;
  stripeAccountEnabled: boolean;
  provisionedCustomersLimit: number;
  provisionedCustomersCount: number;
  createdDate: string | null;
}

interface WhitelabelDetail {
  id: string;
  uid: string;
  ownerEmail: string | null;
  ownerName: string | null;
  owners: Array<{ uid: string; email: string; displayName: string | null }>;
  domain: string | null;
  active: boolean;
  settings: Record<string, unknown> | null;
  domainStatus: string | null;
  domainProvisioned: boolean;
  domainProvider: string | null;
  domainError: string | null;
  stripeAccountId: string | null;
  stripeAccountEnabled: boolean;
  stripeAccountStatus: string | null;
  provisionedCustomersLimit: number;
  provisionedCustomersCount: number;
  termsUrl: string | null;
  privacyUrl: string | null;
  salesEmail: string | null;
  supportUrl: string | null;
  pricingUrl: string | null;
  smtpEnabled: boolean;
  customSubscriptionEnabled: boolean;
  disableFreeAccounts: boolean;
  agreementAccepted: boolean;
  agreementVersion: string | null;
  createdDate: string | null;
  updatedDate: string | null;
}

interface UserOption {
  id: string;
  email: string;
  displayName: string | null;
  photo: string | null;
}

interface WhitelabelManagementProps {
  getToken: () => Promise<string>;
}

const WhitelabelManagement: React.FC<WhitelabelManagementProps> = ({ getToken }) => {
  const [whitelabels, setWhitelabels] = useState<Whitelabel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // Search
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

  // Copy ID feedback
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Menu
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuWhitelabel, setMenuWhitelabel] = useState<Whitelabel | null>(null);

  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingWhitelabel, setEditingWhitelabel] = useState<WhitelabelDetail | null>(null);
  const [saving, setSaving] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    domain: "",
    uid: "",
    active: true,
    provisionedCustomersLimit: 0,
  });

  // Owner search
  const [ownerSearchQuery, setOwnerSearchQuery] = useState("");
  const [ownerOptions, setOwnerOptions] = useState<UserOption[]>([]);
  const [ownerSearchLoading, setOwnerSearchLoading] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState<UserOption | null>(null);
  const [ownerSearchTimeout, setOwnerSearchTimeout] = useState<NodeJS.Timeout | null>(null);

  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingWhitelabel, setDeletingWhitelabel] = useState<Whitelabel | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchWhitelabels = async (search?: string) => {
    setLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const params: Record<string, string | number> = {
        page: page + 1,
        perPage: rowsPerPage,
      };

      if (search) {
        params.search = search;
      }

      const response = await axios.get(`${hostname}/whitelabel/admin`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      setWhitelabels(response.data.whitelabels);
      setTotalCount(response.data.pagination.total);
    } catch (err) {
      setError(parseError(err));
    } finally {
      setLoading(false);
    }
  };

  const fetchWhitelabelDetail = async (whitelabelId: string) => {
    setLoadingDetail(true);
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/whitelabel/admin/${whitelabelId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.whitelabel;
    } catch (err) {
      setError(parseError(err));
      return null;
    } finally {
      setLoadingDetail(false);
    }
  };

  const searchUsers = async (query: string) => {
    if (!query || query.length < 2) {
      setOwnerOptions([]);
      return;
    }

    setOwnerSearchLoading(true);
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/users/search`, {
        params: { query },
        headers: { Authorization: `Bearer ${token}` },
      });
      setOwnerOptions(response.data.users || []);
    } catch (err) {
      console.error("Failed to search users:", err);
      setOwnerOptions([]);
    } finally {
      setOwnerSearchLoading(false);
    }
  };

  useEffect(() => {
    fetchWhitelabels(searchQuery);
  }, [page, rowsPerPage]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      setPage(0);
      fetchWhitelabels(value);
    }, 300);

    setSearchTimeout(timeout);
  };

  const handleOwnerSearchChange = (value: string) => {
    setOwnerSearchQuery(value);

    if (ownerSearchTimeout) {
      clearTimeout(ownerSearchTimeout);
    }

    const timeout = setTimeout(() => {
      searchUsers(value);
    }, 300);

    setOwnerSearchTimeout(timeout);
  };

  // Menu handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, whitelabel: Whitelabel) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuWhitelabel(whitelabel);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuWhitelabel(null);
  };

  const handleEditWhitelabel = async (whitelabel: Whitelabel) => {
    handleMenuClose();

    const detail = await fetchWhitelabelDetail(whitelabel.id);
    if (!detail) return;

    setEditingWhitelabel(detail);

    // Get name from settings
    const name = detail.settings
      ? (detail.settings as Record<string, string>).name ||
        (detail.settings as Record<string, string>).companyName ||
        ""
      : "";

    setEditForm({
      name,
      domain: detail.domain || "",
      uid: detail.uid,
      active: detail.active,
      provisionedCustomersLimit: detail.provisionedCustomersLimit,
    });

    // Set current owner
    if (detail.uid) {
      setSelectedOwner({
        id: detail.uid,
        email: detail.ownerEmail || "",
        displayName: detail.ownerName,
        photo: null,
      });
    } else {
      setSelectedOwner(null);
    }

    setOwnerOptions([]);
    setOwnerSearchQuery("");
    setEditDialogOpen(true);
  };

  const handleSaveWhitelabel = async () => {
    if (!editingWhitelabel) return;

    setSaving(true);
    setError(null);

    try {
      const token = await getToken();
      await axios.put(
        `${hostname}/whitelabel/admin/${editingWhitelabel.id}`,
        {
          active: editForm.active,
          domain: editForm.domain || null,
          uid: editForm.uid,
          provisionedCustomersLimit: editForm.provisionedCustomersLimit,
          settings: {
            name: editForm.name,
          },
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setSuccess("Whitelabel updated successfully");
      setEditDialogOpen(false);
      fetchWhitelabels(searchQuery);
    } catch (err) {
      setError(parseError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (whitelabel: Whitelabel) => {
    setDeletingWhitelabel(whitelabel);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!deletingWhitelabel) return;

    setDeleting(true);
    setError(null);

    try {
      const token = await getToken();
      await axios.delete(`${hostname}/whitelabel/admin/${deletingWhitelabel.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSuccess("Whitelabel deleted successfully");
      setDeleteDialogOpen(false);
      setDeletingWhitelabel(null);
      fetchWhitelabels(searchQuery);
    } catch (err) {
      setError(parseError(err));
    } finally {
      setDeleting(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Unknown";
    return moment(dateString).format("MMM D, YYYY");
  };

  const handleCopyId = (id: string) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getDomainStatusColor = (status: string | null, provisioned: boolean) => {
    if (!status) return "default";
    if (status === "active" && provisioned) return "primary";
    if (status === "pending") return "warning";
    if (status === "failed") return "error";
    return "default";
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
        Whitelabel Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <TextField
          placeholder="Search by name, domain, ID, or owner UID..."
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          size="small"
          sx={{ width: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Whitelabels Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Domain</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Users</TableCell>
                <TableCell>Companies</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {whitelabels.map((whitelabel) => (
                <TableRow key={whitelabel.id} hover>
                  <TableCell>
                    <Tooltip
                      title={copiedId === whitelabel.id ? "Copied!" : "Click to copy"}
                      placement="top"
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          cursor: "pointer",
                          "&:hover": { color: "primary.main" },
                        }}
                        onClick={() => handleCopyId(whitelabel.id)}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "monospace",
                            maxWidth: 80,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {whitelabel.id.substring(0, 8)}...
                        </Typography>
                        <ContentCopyIcon sx={{ fontSize: 14, opacity: 0.6 }} />
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <LanguageIcon color="action" />
                      <Typography variant="body2" fontWeight={500}>
                        {whitelabel.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {whitelabel.domain ? (
                      <Box>
                        <Typography variant="body2">{whitelabel.domain}</Typography>
                        <Chip
                          label={whitelabel.domainStatus || "manual"}
                          size="small"
                          color={getDomainStatusColor(
                            whitelabel.domainStatus,
                            whitelabel.domainProvisioned
                          )}
                          variant="outlined"
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No domain
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {whitelabel.ownerName || "No name"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {whitelabel.ownerEmail || whitelabel.uid}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={<PeopleIcon />}
                      label={whitelabel.userCount}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={<BusinessIcon />}
                      label={whitelabel.companyCount}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      <Chip
                        label={whitelabel.active ? "Active" : "Inactive"}
                        size="small"
                        color={whitelabel.active ? "primary" : "default"}
                      />
                      {whitelabel.stripeAccountEnabled && (
                        <Chip label="Stripe" size="small" color="info" variant="outlined" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDate(whitelabel.createdDate)}</Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={(e) => handleMenuOpen(e, whitelabel)}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {whitelabels.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No whitelabels found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}

      {/* Actions Menu */}
      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => menuWhitelabel && handleEditWhitelabel(menuWhitelabel)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Whitelabel</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => menuWhitelabel && handleDeleteClick(menuWhitelabel)}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete Whitelabel</ListItemText>
        </MenuItem>
      </Menu>

      {/* Edit Whitelabel Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            Edit Whitelabel
            <IconButton onClick={() => setEditDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingDetail ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            editingWhitelabel && (
              <Box sx={{ pt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Whitelabel ID: {editingWhitelabel.id}
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Name"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Domain"
                      value={editForm.domain}
                      onChange={(e) => setEditForm({ ...editForm, domain: e.target.value })}
                      helperText="Custom domain for this whitelabel instance"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      value={selectedOwner}
                      onChange={(event, newValue) => {
                        setSelectedOwner(newValue);
                        setEditForm({
                          ...editForm,
                          uid: newValue?.id || "",
                        });
                      }}
                      inputValue={ownerSearchQuery}
                      onInputChange={(event, newInputValue, reason) => {
                        if (reason === "input") {
                          handleOwnerSearchChange(newInputValue);
                        }
                      }}
                      options={ownerOptions}
                      loading={ownerSearchLoading}
                      getOptionLabel={(option) => option.displayName || option.email || option.id}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      filterOptions={(x) => x}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Owner"
                          placeholder="Search by email or name..."
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {ownerSearchLoading ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                          helperText="Search for a user to transfer ownership"
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Avatar src={option.photo || undefined} sx={{ width: 32, height: 32 }}>
                              {option.displayName?.[0] || option.email?.[0] || <PersonIcon />}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={500}>
                                {option.displayName || "No name"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {option.email}
                              </Typography>
                            </Box>
                          </Box>
                        </li>
                      )}
                      noOptionsText={
                        ownerSearchQuery.length < 2
                          ? "Type at least 2 characters to search"
                          : "No users found"
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Provisioned Customers Limit"
                      type="number"
                      value={editForm.provisionedCustomersLimit}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          provisionedCustomersLimit: parseInt(e.target.value) || 0,
                        })
                      }
                      helperText="Maximum number of customers this whitelabel can provision"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={editForm.active}
                          onChange={(e) => setEditForm({ ...editForm, active: e.target.checked })}
                        />
                      }
                      label="Active"
                    />
                    <Typography variant="caption" display="block" color="text.secondary">
                      When disabled, the whitelabel instance is hidden and cannot be accessed
                    </Typography>
                  </Grid>

                  {editingWhitelabel.owners && editingWhitelabel.owners.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Additional Owners
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {editingWhitelabel.owners.map((owner) => (
                          <Chip
                            key={owner.uid}
                            label={owner.displayName || owner.email}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <LoadingButton onClick={() => setEditDialogOpen(false)}>Cancel</LoadingButton>
          <LoadingButton variant="contained" onClick={handleSaveWhitelabel} loading={saving}>
            Save Changes
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs">
        <DialogTitle>Delete Whitelabel</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the whitelabel "{deletingWhitelabel?.name}"? This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <LoadingButton onClick={() => setDeleteDialogOpen(false)}>Cancel</LoadingButton>
          <LoadingButton
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            loading={deleting}
          >
            Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WhitelabelManagement;
